import { type ReactElement, useState } from 'react'
import {
  CompanyInlineSelect,
  DisplayUsers,
  MaterialSymbolIcon,
  Page,
  TButton,
  ToggleColumns,
  useUserListColumns,
} from '@components'
import { DownloadBetaUsersCsv } from './DownloadBetaUsersCsv'
import { useProfile, useTranslation, useUserListState } from '@front/model'
import { Group, Switch, TextInput } from '@mantine/core'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { ALL_COMPANIES, type CompanyId } from '@terros/common'
import { parseAsCompanyId } from '@util'
import { type SortingState } from '@tanstack/react-table'
import { useQueryState } from 'nuqs'

function BetaUserListRoute(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isTantalim } = useProfile()

  const [selectedCompanyId, setSelectedCompanyId] = useQueryState('company', parseAsCompanyId)
  const targetCompanyId = selectedCompanyId ?? ALL_COMPANIES

  const state = useUserListState({
    show: 'company',
    companyId: targetCompanyId,
    showBeta: true,
  })

  const [sorting, setSorting] = useState<SortingState>([{ id: 'lastAccess', desc: true }])

  const { columns, selectedColumns, setSelectedColumns, toggleableColumns, resetColumnsToggle } = useUserListColumns({
    showCompanyColumn: isTantalim,
  })

  const reset = (): void => {
    resetColumnsToggle()
    state.setFilter('')
  }

  const { showArchived, setShowArchived, refetch, isFetching } = state

  const handleCompanySelect = (companyId?: CompanyId): void => {
    setSelectedCompanyId(companyId ?? ALL_COMPANIES)
  }

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Beta Users')} />
      <Page.FunctionBar>
        <Group>
          <TextInput
            placeholder={t('Search users...')}
            value={state.filter}
            onChange={(e) => state.setFilter(e.currentTarget.value)}
            leftSection={<MaterialSymbolIcon icon='search' />}
          />
          <CompanyInlineSelect
            allowDeselect
            placeholder={t('All Companies')}
            value={selectedCompanyId && selectedCompanyId !== ALL_COMPANIES ? selectedCompanyId : undefined}
            onChange={handleCompanySelect}
          />
          <ToggleColumns columns={toggleableColumns} selectedColumns={selectedColumns} setSelectedColumns={setSelectedColumns} />
          <TButton icon='refresh' variant='secondary' onClick={reset}>
            {t('Reset')}
          </TButton>
          <DownloadBetaUsersCsv data={state.filtered} />
        </Group>
        <Switch
          checked={showArchived}
          label={t('Show Archived')}
          labelPosition='left'
          onChange={(event) => setShowArchived(event.currentTarget.checked)}
        />
      </Page.FunctionBar>
      <Page.Content>
        <DisplayUsers
          columns={columns}
          sorting={sorting}
          setSorting={setSorting}
          state={state}
          onRowClick={(user) => {
            void navigate({ to: `/betaUsers/${user.userId}` })
          }}
        />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/betaUsers/')({
  component: BetaUserListRoute,
})
